import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plug, RefreshCw, KeyRound, CheckCircle2, AlertCircle, Building2 } from "lucide-react";

function Tag({ children, mono, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 text-slate-600 border-slate-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${tones[tone]} ${mono ? "font-mono" : ""}`}>
      {children}
    </span>
  );
}

export default function BokunCuentas({ onChanged, onSyncStarted, syncing, refreshKey }) {
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bokun/cuentas");
      setCuentas(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const setBusyFor = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  const toggleActivo = async (c) => {
    if (!c.activo && !c.credenciales_cargadas) {
      toast.error("Cargá primero las credenciales (variables de entorno) de esta cuenta.");
      return;
    }
    try {
      await api.put(`/bokun/cuentas/${c.id}`, { activo: !c.activo });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const cambiarEntorno = async (c, entorno) => {
    try {
      await api.put(`/bokun/cuentas/${c.id}`, { entorno });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const probar = async (c) => {
    setBusyFor(c.id, "test");
    toast.info(`Probando conexión con ${c.nombre}...`);
    try {
      const { data } = await api.post(`/bokun/cuentas/${c.id}/test`);
      if (data.ok) toast.success(`Conexión OK con ${c.nombre}.`);
      else toast.error(data.error);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyFor(c.id, null);
    }
  };

  const sincronizar = async (c) => {
    setBusyFor(c.id, "sync");
    try {
      await api.post(`/bokun/cuentas/${c.id}/sync`, {});
      toast.info(`Sincronización de ${c.nombre} iniciada en segundo plano…`);
      onSyncStarted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyFor(c.id, null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-slate-50 border-slate-200">
        <div className="flex gap-3">
          <KeyRound className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600">
            Cada cuenta de Bokun autentica con <strong>Access Key + Secret Key</strong> (firma HMAC-SHA1). Las claves se
            cargan como variables de entorno indicadas en cada tarjeta; nunca se guardan en la base de datos. Una vez
            cargadas, activá la cuenta y el sistema sincronizará reservas nuevas y cambios de estado automáticamente.
          </p>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {cuentas.map((c) => (
          <Card key={c.id} className="p-4" data-testid={`bokun-cuenta-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white shrink-0">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{c.nombre}</p>
                  <p className="text-[12px] text-slate-500 truncate">{c.usuario}</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {!c.credenciales_cargadas ? (
                      <Tag tone="amber"><AlertCircle className="h-3 w-3" /> No conectado · pendiente de credenciales</Tag>
                    ) : c.last_error ? (
                      <Tag tone="red"><AlertCircle className="h-3 w-3" /> Error de conexión</Tag>
                    ) : c.activo ? (
                      <Tag tone="green"><CheckCircle2 className="h-3 w-3" /> Conectado</Tag>
                    ) : (
                      <Tag tone="slate"><CheckCircle2 className="h-3 w-3" /> Credenciales OK (inactiva)</Tag>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    <Tag mono>{c.accesskey_env}</Tag>
                    <Tag mono>{c.secret_env}</Tag>
                  </div>
                  {c.last_error && <p className="text-[11px] text-red-600 mt-1">⚠ {c.last_error}</p>}
                  {c.last_synced && (
                    <p className="text-[11px] text-slate-400 mt-1">
                      Última sync: {new Date(c.last_synced).toLocaleString("es")}
                    </p>
                  )}
                </div>
              </div>
              <Switch
                checked={c.activo}
                onCheckedChange={() => toggleActivo(c)}
                data-testid={`bokun-cuenta-activo-${c.id}`}
              />
            </div>

            <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-100">
              <Select value={c.entorno} onValueChange={(v) => cambiarEntorno(c, v)}>
                <SelectTrigger className="h-8 w-28 text-xs" data-testid={`bokun-cuenta-entorno-${c.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prod">Producción</SelectItem>
                  <SelectItem value="test">Pruebas</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => probar(c)} disabled={busy[c.id]} data-testid={`bokun-cuenta-test-${c.id}`} className="gap-1 text-slate-600">
                  {busy[c.id] === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Probar
                </Button>
                <Button variant="ghost" size="sm" onClick={() => sincronizar(c)} disabled={busy[c.id] || syncing} data-testid={`bokun-cuenta-sync-${c.id}`} className="gap-1 text-slate-600">
                  {busy[c.id] === "sync" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sincronizar
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
